import { FormattedMessage, useIntl } from 'react-intl'
import { LabelWithIndicator, Typography } from '@carto/meridian-ds/components'
import { HelpOutline } from '@mui/icons-material'
import { Box, Divider, FormControlLabel, Paper, Switch, Tooltip, styled } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'app/state'
import { FeatureFlag } from 'app/types'
import { useFeatureFlag } from 'app/ui/useFeatureFlag'
import useUserPermissions from 'features/account/state/useUserPermissions'
import { getAgent, setAgentEnabledForViewer, updateMapSettings } from 'features/maps/state/mapsSlice'
import { MapSettings } from 'features/maps/types'
import { useBasemaps } from 'features/builder/hooks/builderMapHooks'
import { mapCommentsEnabledSelector } from 'features/builder/model/mapComments'
import { useGovernanceMaps } from 'features/common/hooks/useGovernanceMaps'

const RootPaper = styled(Paper)(({ theme }) => ({
  width: '320px',
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 0)
}))

const SectionTitle = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 0, 0.5, 1.5),
  ...theme.typography.caption,
  color: theme.palette.text.secondary
}))

const MapSettingFormControl = styled(FormControlLabel)(({ theme }) => ({
  justifyContent: 'space-between',
  width: '100%',
  padding: theme.spacing(0.75, 2)
}))

function MapSettingSwitch({ propName, disabled }: { propName: keyof MapSettings; disabled?: boolean }) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const value = useAppSelector((state) => state.maps.mapSettings[propName] && !disabled)
  const tooltip = intl.formatMessage({ id: `builder.mapSettings.propsTooltips.${propName}` })

  const handleChange = () => {
    dispatch(updateMapSettings({ [propName]: !value }))
  }

  return (
    <Box>
      <MapSettingFormControl
        disabled={disabled}
        aria-label={propName}
        labelPlacement="start"
        control={<Switch name={propName} checked={Boolean(value)} onChange={handleChange} color="primary" />}
        label={
          <LabelWithIndicator
            label={<FormattedMessage id={`builder.mapSettings.props.${propName}`} />}
            icon={
              <Tooltip title={tooltip}>
                <HelpOutline />
              </Tooltip>
            }
          />
        }
      />
    </Box>
  )
}

function SharedMapSettingsPane() {
  const dispatch = useAppDispatch()
  const { exportViewportDataAvailable, exportPdfReportAvailable } = useUserPermissions()
  const commentsEnabled = useAppSelector(mapCommentsEnabledSelector)
  const hasSqlParameters = useAppSelector((state) => state.builder.sqlParameters.length > 0)
  const agent = useAppSelector(getAgent)
  const { mapStyles } = useBasemaps()
  const basemapSelectorEnabled = useFeatureFlag(FeatureFlag.BasemapSelectorInLegend)
  const { allowUsersToManagePDFReports, allowUsersToManageViewportDataExport } = useGovernanceMaps()

  const isAgentToggleDisabled = !agent?.config || !agent?.config.model

  return (
    <RootPaper>
      <Box py={1} px={2}>
        <Typography variant="body2" weight="strong">
          <FormattedMessage id="builder.mapSettings.title" />
        </Typography>
      </Box>
      <Divider />
      <Box>
        <SectionTitle>
          <FormattedMessage id="builder.mapSettings.ai" />
        </SectionTitle>
        {agent && (
          <MapSettingFormControl
            disabled={isAgentToggleDisabled}
            labelPlacement="start"
            control={
              <Switch
                name="agent"
                disabled={isAgentToggleDisabled}
                checked={agent.enabledForViewer}
                onChange={() => {
                  dispatch(setAgentEnabledForViewer(!agent.enabledForViewer))
                }}
                color="primary"
              />
            }
            label={
              <LabelWithIndicator
                label={<FormattedMessage id={'builder.mapSettings.props.agent'} />}
                icon={
                  <Tooltip
                    title={
                      isAgentToggleDisabled ? (
                        <FormattedMessage id={'builder.mapSettings.propsTooltips.agentDisabled'} />
                      ) : (
                        <FormattedMessage id={'builder.mapSettings.propsTooltips.agent'} />
                      )
                    }
                  >
                    <HelpOutline />
                  </Tooltip>
                }
              />
            }
          />
        )}
      </Box>
      <Divider />
      <Box>
        <SectionTitle>
          <FormattedMessage id="builder.mapSettings.interactivity" />
        </SectionTitle>
        <MapSettingSwitch propName="featureSelectionTool" />
        <MapSettingSwitch propName="sqlParameterControls" disabled={!hasSqlParameters} />
        {allowUsersToManageViewportDataExport && (
          <MapSettingSwitch propName="exportViewportData" disabled={!exportViewportDataAvailable} />
        )}
        {allowUsersToManagePDFReports && <MapSettingSwitch propName="exportPDF" disabled={!exportPdfReportAvailable} />}
        {commentsEnabled && <MapSettingSwitch propName="comments" />}
      </Box>
      <Divider />
      <Box>
        <SectionTitle>
          <FormattedMessage id="builder.mapSettings.navigation" />
        </SectionTitle>
        <MapSettingSwitch propName="addressSearchBar" />
        <MapSettingSwitch propName="showMeasureDistanceTool" />
        <MapSettingSwitch propName="scrollWheelZoom" />
        <MapSettingSwitch propName="showMyLocationButton" />
      </Box>
      <Divider />
      {basemapSelectorEnabled && (
        <>
          <Box>
            <SectionTitle>
              <FormattedMessage id="builder.mapSettings.basemaps" />
            </SectionTitle>
            <MapSettingSwitch propName="basemapsSelector" disabled={mapStyles.length <= 1} />
          </Box>
          <Divider />
        </>
      )}
      <Box>
        <SectionTitle>
          <FormattedMessage id="builder.mapSettings.display" />
        </SectionTitle>
        <MapSettingSwitch propName="showTitlePublicMap" />
      </Box>
      <Divider />
      <Box>
        <SectionTitle>
          <FormattedMessage id="builder.mapSettings.notifications" />
        </SectionTitle>
        <MapSettingSwitch propName="showPerformanceWarnings" />
      </Box>
    </RootPaper>
  )
}

export default SharedMapSettingsPane
